import { MutationOptions, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '~/hooks';
import {
  changePasswordApi,
  ChangePasswordData,
  ChangePasswordResponse,
  revokeSessionApi,
  verify2FAApi,
  Verify2FAData,
  Verify2FAResponse,
  disable2FAApi,
  Disable2FAResponse,
} from '../repository';
import { PROFILE_QUERY_KEYS } from '../queries/user';
import { SECURITY_QUERY_KEYS } from '../queries/security';

/**
 * Mutation hook for changing password
 * @param options - Additional mutation options
 * @returns React Query mutation result
 */
export const useChangePasswordMutation = (
  options?: MutationOptions<
    ChangePasswordResponse,
    CustomAxiosError,
    ChangePasswordData
  >,
) => {
  return useApiMutation({
    ...options,
    mutationFn: (data: ChangePasswordData) => changePasswordApi(data),
    onSuccess: (...rest) => {
      options?.onSuccess?.(...rest);
    },
  });
};

/**
 * Mutation hook for revoking a session
 * @param options - Additional mutation options
 * @returns React Query mutation result
 */
export const useRevokeSessionMutation = (
  options?: MutationOptions<void, CustomAxiosError, string>,
) => {
  const queryClient = useQueryClient();

  return useApiMutation({
    ...options,
    mutationFn: (sessionId: string) => revokeSessionApi(sessionId),
    onSuccess: async (...rest) => {
      // Invalidate sessions query to refetch updated sessions list
      await queryClient.invalidateQueries({
        queryKey: [SECURITY_QUERY_KEYS.ACTIVE_SESSIONS],
      });
      options?.onSuccess?.(...rest);
    },
  });
};

/**
 * Mutation hook for verifying and enabling 2FA
 * @param options - Additional mutation options
 * @returns React Query mutation result
 */
export const useVerify2FAMutation = (
  options?: MutationOptions<Verify2FAResponse, CustomAxiosError, Verify2FAData>,
) => {
  const queryClient = useQueryClient();

  return useApiMutation({
    ...options,
    mutationFn: (data: Verify2FAData) => verify2FAApi(data),
    onSuccess: async (...rest) => {
      // Invalidate profile query to get updated 2FA status
      await queryClient.invalidateQueries({
        queryKey: [PROFILE_QUERY_KEYS.PROFILE],
      });
      options?.onSuccess?.(...rest);
    },
  });
};

/**
 * Mutation hook for disabling 2FA
 * @param options - Additional mutation options
 * @returns React Query mutation result
 */
export const useDisable2FAMutation = (
  options?: MutationOptions<Disable2FAResponse, CustomAxiosError, void>,
) => {
  const queryClient = useQueryClient();

  return useApiMutation({
    ...options,
    mutationFn: () => disable2FAApi(),
    onSuccess: async (...rest) => {
      // Invalidate profile query to get updated 2FA status
      await queryClient.invalidateQueries({
        queryKey: [PROFILE_QUERY_KEYS.PROFILE],
      });
      options?.onSuccess?.(...rest);
    },
  });
};
